import { useEffect, useRef, useState } from "react";
import { Ban, Droplets, Upload, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { upload } from "@/lib/api";
import { createFx } from "@/components/meeting/videoFx";
import { productLogo } from "@/components/meeting/MeetingSetupFields";

const KEY = "sa_meeting_bg";

export const useVideoFx = (m) => {
  const fx = useRef(null);
  const [mode, setMode] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)) || { type: "none" }; } catch { return { type: "none" }; } });
  const [ready, setReady] = useState(false);
  const raw = m.rawCam;
  useEffect(() => {
    if (!raw) return undefined;
    let f;
    try { f = createFx(raw); } catch { return undefined; }
    fx.current = f; m.fxOn.current = true; m.applyVideo(f.track); setReady(true);
    return () => { f.stop(); fx.current = null; m.fxOn.current = false; setReady(false); };
  }, [raw]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ready) return;
    localStorage.setItem(KEY, JSON.stringify(mode));
    fx.current?.setMode(mode).catch(() => { toast.error("That background couldn't load on this device"); setMode({ type: "none" }); });
  }, [mode, ready]);
  useEffect(() => { if (ready) fx.current?.setLogo(productLogo(m.me?.product_id)).catch(() => {}); }, [m.me?.product_id, ready]);
  return { mode, setMode, available: ready };
};

const Opt = ({ active, onClick, children, label, testId }) => (
  <button onClick={onClick} className={`relative w-full aspect-video rounded-lg overflow-hidden border-2 flex flex-col items-center justify-center text-[11px] gap-1 bg-slate-800 transition-colors ${active ? "border-blue-500" : "border-transparent hover:border-white/30"}`} data-testid={testId}>
    {children}<span className="absolute bottom-0 inset-x-0 bg-black/60 truncate px-1 py-0.5">{label}</span>
    {active && <Check className="absolute top-1 right-1 w-4 h-4 text-blue-400" />}
  </button>
);

export const BackgroundPanel = ({ fx, isHost }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const load = () => api.get("/api/meeting-backgrounds").then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const up = async (f) => {
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await upload("/api/meeting-backgrounds", fd); await load(); fx.setMode({ type: "image", url: data.url, id: data.id }); toast.success(data.scope === "library" ? "Added for everyone" : "Added to your backgrounds"); }
    catch (e) { toast.error(e.response?.status === 401 ? "Sign in to upload your own background" : e.response?.data?.detail || "Upload failed"); }
    setBusy(false); file.current.value = "";
  };
  const del = async (b) => { try { await api.delete(`/api/meeting-backgrounds/${b.id}`); if (fx.mode.id === b.id) fx.setMode({ type: "none" }); load(); } catch { toast.error("Couldn't remove it"); } };
  const sel = (t, extra = {}) => fx.setMode({ type: t, ...extra });
  return (
    <div className="space-y-4" data-testid="background-panel">
      {!fx.available && <p className="text-xs text-amber-300">Turn your camera on to use backgrounds.</p>}
      <div className="grid grid-cols-3 gap-2">
        <Opt active={fx.mode.type === "none"} onClick={() => sel("none")} label="None" testId="bg-none"><Ban className="w-5 h-5" /></Opt>
        <Opt active={fx.mode.type === "blur-light"} onClick={() => sel("blur-light")} label="Light blur" testId="bg-blur-light"><Droplets className="w-5 h-5" /></Opt>
        <Opt active={fx.mode.type === "blur-strong"} onClick={() => sel("blur-strong")} label="Strong blur" testId="bg-blur-strong"><Droplets className="w-6 h-6 text-blue-300" /></Opt>
        {list.map(b => (
          <div key={b.id} className="relative group">
            <Opt active={fx.mode.id === b.id} onClick={() => sel("image", { url: b.url, id: b.id })} label={b.name} testId={`bg-${b.id}`}><img src={b.url} alt="" className="absolute inset-0 w-full h-full object-cover" /></Opt>
            {b.can_delete && <button onClick={() => del(b)} className="absolute top-1 left-1 bg-black/70 hover:bg-red-600 rounded p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100" title="Remove" data-testid={`bg-delete-${b.id}`}><Trash2 className="w-3 h-3" /></button>}
          </div>
        ))}
        <button onClick={() => file.current.click()} disabled={busy} className="aspect-video rounded-lg border-2 border-dashed border-white/30 hover:border-blue-400 flex flex-col items-center justify-center text-[11px] gap-1" data-testid="bg-upload-btn">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}{isHost ? "Add for everyone" : "Upload yours"}</button>
        <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => up(e.target.files[0])} data-testid="bg-upload-input" />
      </div>
      <p className="text-[11px] text-slate-400">Only you choose your background. {isHost ? "Pictures you upload here are added to everyone's list." : "Pictures you upload are only for you."} The meeting's logo always shows on your video.</p>
    </div>
  );
};
